import React from "react";
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Fab,
  IconButton,
  Checkbox,
  FormGroup,
  FormControlLabel,
  TextField,
  Grid,
  Divider,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import NewOrgDialog from "./components/NewOrgDialog";
import NewAdminUserDialog from "./components/NewAdminUserDialog";
import EditAdminUserDialog from "./components/EditAdminUserDialog";
import Deployments from "./components/Deployments";
import Licensing from "./components/Licensing";
import { EventContext } from "../../contexts/EventContext";
import request from "../../utilities/requests";

const SIDEBAR_WIDTH = 280;

const Admin = () => {
  const { userData } = React.useContext(EventContext);
  const [orgs, setOrgs] = React.useState([]);
  const [orgSearch, setOrgSearch] = React.useState("");
  const [users, setUsers] = React.useState([]);
  const [newOrgOpen, setNewOrgOpen] = React.useState(false);
  const [selectedOrg, setSelectedOrg] = React.useState(null);
  const [editOrg, setEditOrg] = React.useState(null);
  const [newUserOpen, setNewUserOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [editUserOpen, setEditUserOpen] = React.useState(false);
  const [showLicensing, setShowLicensing] = React.useState(false);

  React.useEffect(() => {
    if (orgs.length === 0) {
      getOrgs();
    }
  });

  React.useEffect(() => {
    if (selectedOrg) {
      getUsers();
    }
  }, [selectedOrg]);

  React.useEffect(() => {
    if (
      userData &&
      (userData?.role === "superadmin" ||
        userData?.permissions?.canViewContract === true ||
        userData?.permissions?.canViewInvoicing === true)
    ) {
      setShowLicensing(true);
    }
  }, [userData]);

  const getOrgs = async () => {
    const result = await request("orgs");
    if (result.success) {
      if (result.orgs) {
        setOrgs([...result.orgs]);
      } else if (result.org) {
        setOrgs([result.org]);
        setSelectedOrg(result?.org);
      }
    }
  };

  const getUsers = async () => {
    const result = await request(`users/adminusers?orgId=${selectedOrg._id}`);
    if (result.success) {
      setUsers(result.users);
    }
  };

  const updateServices = async (name) => {
    const value = !selectedOrg[name];
    const body = { orgId: selectedOrg._id, [name]: value };
    const updateResult = await request("orgs/update", "post", body);
    if (updateResult?.success) {
      setSelectedOrg({ ...selectedOrg, [name]: value });
    }
  };

  const canEditOrg =
    userData?.permissions?.canEditOrg || userData?.role === "superadmin";

  const filteredOrgs = orgs.filter((org) =>
    org.name?.toLowerCase().includes(orgSearch.toLowerCase())
  );

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 64px)" }}>
      {/* ── Left sidebar ── */}
      <Paper
        elevation={2}
        square
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          borderRight: 1,
          borderColor: "divider",
          position: "sticky",
          top: 0,
          height: "100%",
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Orgs
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Search orgs…"
            value={orgSearch}
            onChange={(e) => setOrgSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Divider />
        <List sx={{ flexGrow: 1, overflowY: "auto", py: 0 }}>
          {filteredOrgs.map((org) => (
            <ListItem
              key={org._id}
              disablePadding
              secondaryAction={
                canEditOrg ? (
                  <IconButton
                    size="small"
                    edge="end"
                    sx={{ mr: 0.5 }}
                    onClick={() => {
                      setEditOrg(org);
                      setNewOrgOpen(true);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                ) : null
              }
              sx={{
                borderLeft: 3,
                borderColor:
                  selectedOrg?._id === org._id ? "primary.main" : "transparent",
                bgcolor:
                  selectedOrg?._id === org._id ? "action.selected" : "transparent",
              }}
            >
              <ListItemButton
                onClick={() => setSelectedOrg(org)}
                sx={{ pr: canEditOrg ? 5 : 2 }}
              >
                <ListItemText
                  primary={org.name}
                  primaryTypographyProps={{ variant: "body2" }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {userData?.role === "superadmin" && (
          <>
            <Divider />
            <Box sx={{ p: 1.5, display: "flex", justifyContent: "flex-end" }}>
              <Fab
                size="small"
                color="primary"
                onClick={() => {
                  setEditOrg(null);
                  setNewOrgOpen(true);
                }}
              >
                <AddIcon />
              </Fab>
            </Box>
          </>
        )}
      </Paper>

      {/* ── Right detail panel ── */}
      <Box sx={{ flexGrow: 1, overflowY: "auto", p: 3 }}>
        {!selectedOrg ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography color="text.secondary">Select an org to get started</Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Users */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2, position: "relative" }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {selectedOrg.name} Users
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <List dense disablePadding>
                  {users.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No users yet.
                    </Typography>
                  ) : (
                    users.map((user) => (
                      <ListItem key={user._id} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            setSelectedUser(user);
                            setEditUserOpen(true);
                          }}
                        >
                          <ListItemText
                            primary={`${user?.profile?.firstName ?? ""} ${user?.profile?.lastName ?? ""}`.trim() || user.email}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                  )}
                </List>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                  <Fab
                    size="small"
                    color="primary"
                    onClick={() => setNewUserOpen(true)}
                  >
                    <AddIcon />
                  </Fab>
                </Box>
              </Paper>
            </Grid>

            {/* Services (superadmin only) */}
            {userData?.role === "superadmin" && (
              <Grid item xs={12} md={6}>
                <Paper elevation={2} sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Services
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!selectedOrg.pairingActive}
                          onChange={() => updateServices("pairingActive")}
                        />
                      }
                      label="Pairing"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!selectedOrg.sportsAndWellnessActive}
                          onChange={() => updateServices("sportsAndWellnessActive")}
                        />
                      }
                      label="Sports & Wellness"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!selectedOrg.artsAndEntertainmentActive}
                          onChange={() => updateServices("artsAndEntertainmentActive")}
                        />
                      }
                      label="Arts & Entertainment"
                    />
                  </FormGroup>
                </Paper>
              </Grid>
            )}

            {/* Deployments */}
            <Grid item xs={12} md={6}>
              <Deployments
                deployments={selectedOrg.deployments ?? []}
                refresh={getOrgs}
              />
            </Grid>

            {/* Licensing */}
            {showLicensing && (
              <Grid item xs={12} md={6}>
                <Licensing org={selectedOrg} />
              </Grid>
            )}
          </Grid>
        )}
      </Box>

      <NewOrgDialog
        open={newOrgOpen}
        close={() => {
          setNewOrgOpen(false);
          getOrgs();
        }}
        org={editOrg}
      />
      <NewAdminUserDialog
        open={newUserOpen}
        close={() => setNewUserOpen(false)}
        orgId={selectedOrg?._id ?? null}
        refresh={getUsers}
      />
      <EditAdminUserDialog
        open={editUserOpen}
        close={() => {
          setEditUserOpen(false);
          getUsers();
        }}
        user={selectedUser}
      />
    </Box>
  );
};

export default Admin;
